import { CheckIcon, Icon, SearchIcon } from '@vrplatform/icons';
import {
  Box,
  Center,
  Group,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  type FloatingPosition,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import {
  defaultRangeExtractor,
  type Range,
  type Virtualizer,
  useVirtualizer,
} from '@tanstack/react-virtual';
import {
  type ElementRef,
  type KeyboardEvent as ReactKeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Divider } from '../Divider';
import { Input } from '../input';
import type {
  CustomAction,
  PinnedItems,
  SelectItem,
  SelectValueProps,
} from './_types';

export type DropdownProps = {
  position?: FloatingPosition;
  preventCloseOnSelect?: boolean;
  withinPortal?: boolean;
  noOptionsText?: string;
  width?: number | 'target';
  searchPlaceholder?: string;
  zIndex?: number;
  hideSearch?: boolean;
};

export type InfiniteDataProps<TSelectValue extends string = string> = {
  data:
    | {
        pages: { list: SelectItem<TSelectValue>[]; aggregate: number }[];
        pageParams: unknown[];
      }
    | undefined;
  error: Error | null;
  isFetchingNextPage: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: () => Promise<any>;
  isFetching: boolean;
  setSearch: (search: string) => void;
};

export const SELECT_DROPDOWN_STICKY_GROUP_TEST_ID =
  'select-dropdown-sticky-group';

type RenderRow<TSelectValue extends string = string> =
  | {
      type: 'group';
      key: string;
      label: string;
    }
  | {
      type: 'option';
      key: string;
      item: SelectItem<TSelectValue>;
      optionIndex: number;
    };

type SelectDropdownProps<TSelectValue extends string = string> = {
  value: SelectValueProps<TSelectValue>['value'];
  setValue: SelectValueProps<TSelectValue>['setValue'];
} & InfiniteDataProps<TSelectValue> &
  DropdownProps &
  CustomAction &
  PinnedItems;

export const SelectDropdown = <TSelectValue extends string = string>({
  value,
  setValue,
  data,
  error,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  setSearch,
  noOptionsText = 'Nothing found ...',
  searchPlaceholder = 'Search ...',
  customBottomActions,
  customActionTop,
  hideSearch,
  pinnedItems: pinned,
  isFetching,
}: SelectDropdownProps<TSelectValue>) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const viewportRef = useRef<ElementRef<'div'>>(null);
  const activeStickyGroupRowIndexRef = useRef<number | null>(null);
  const theme = useMantineTheme();
  const { colors } = theme;
  const { colorScheme } = useMantineColorScheme();

  const [searchValue, setSearchValue] = useState('');

  const filteredPinnedItems = useMemo<SelectItem<TSelectValue>[]>(() => {
    const typedPinnedItems = (pinned ?? []) as SelectItem<TSelectValue>[];

    if (!typedPinnedItems.length) return [];
    if (!searchValue.trim()) return typedPinnedItems;

    return typedPinnedItems.filter((item) =>
      item.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [pinned, searchValue]);

  const rows = useMemo<SelectItem<TSelectValue>[]>(() => {
    const list = data?.pages.flatMap((page) => page.list) || [];

    if (!filteredPinnedItems.length) return list;

    return [...filteredPinnedItems, ...list];
  }, [data?.pages, filteredPinnedItems]);

  const pinnedValues = useMemo(
    () => new Set(filteredPinnedItems.map((item) => item.value)),
    [filteredPinnedItems]
  );
  const renderRows = useMemo<RenderRow<TSelectValue>[]>(() => {
    const nextRows: RenderRow<TSelectValue>[] = [];
    let previousGroup: string | null = null;

    rows.forEach((item, optionIndex) => {
      const groupLabel = item.group ?? null;

      if (groupLabel && groupLabel !== previousGroup) {
        nextRows.push({
          type: 'group',
          key: `group-${groupLabel}-${optionIndex}`,
          label: groupLabel,
        });
      }

      nextRows.push({
        type: 'option',
        key: `option-${optionIndex}-${item.value}`,
        item,
        optionIndex,
      });

      previousGroup = groupLabel;
    });

    return nextRows;
  }, [rows]);
  const optionRenderIndexes = useMemo(
    () =>
      renderRows.reduce<number[]>((indexes, row, index) => {
        if (row.type === 'option') {
          indexes[row.optionIndex] = index;
        }

        return indexes;
      }, []),
    [renderRows]
  );

  const totalCount = renderRows.length + (hasNextPage ? 1 : 0);
  const listOffset = customActionTop ? 1 : 0;
  const groupRowIndexes = useMemo(
    () =>
      renderRows.reduce<number[]>((indexes, row, index) => {
        if (row.type === 'group') {
          indexes.push(index);
        }

        return indexes;
      }, []),
    [renderRows]
  );
  const groupRowIndexSet = useMemo(
    () => new Set(groupRowIndexes),
    [groupRowIndexes]
  );
  const estimateRowSize = useCallback(
    (index: number) => {
      if (index >= renderRows.length) return 20;

      const row = renderRows[index];

      if (!row) return 32;
      if (row.type === 'group') return 38;

      return row.item.description ? 48 : 32;
    },
    [renderRows]
  );

  const virtualizer = useVirtualizer({
    count: totalCount,
    getScrollElement: () => viewportRef.current,
    estimateSize: estimateRowSize,
    overscan: 5,
    rangeExtractor: useCallback(
      (range: Range) => {
        if (!groupRowIndexes.length) {
          activeStickyGroupRowIndexRef.current = null;
          return defaultRangeExtractor(range);
        }

        activeStickyGroupRowIndexRef.current =
          [...groupRowIndexes]
            .reverse()
            .find((index) => range.startIndex >= index) ?? null;

        const next = new Set(defaultRangeExtractor(range));

        if (activeStickyGroupRowIndexRef.current !== null) {
          next.add(activeStickyGroupRowIndexRef.current);
        }

        return [...next].sort((a, b) => a - b);
      },
      [groupRowIndexes]
    ),
  });

  const virtualItems = virtualizer.getVirtualItems();
  const listHeight = virtualizer.getTotalSize();

  const showSkeletons = isFetching && !isFetchingNextPage;
  const defaultHeight = 300;
  const parentHeight = useMemo(() => {
    if (showSkeletons) return 80;

    const size = listHeight || 50;
    return size > defaultHeight ? defaultHeight : size;
  }, [showSkeletons, listHeight]);

  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];

    if (!lastItem) return;

    if (
      lastItem.index >= renderRows.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    virtualItems,
    renderRows.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  const { highlightedIndex, buttonRefs, onKeyDown } = useKeyboardNavigation({
    items: rows,
    optionRenderIndexes,
    listOffset,
    virtualizer,
    customTopActionSubmit: customActionTop?.onSubmit,
    customBottomActions,
    search: searchValue,
  });

  const borderColor = colors.gray[colorScheme === 'dark' ? 7 : 2];

  const handleSearchChange = useCallback(
    (nextSearch: string) => {
      setSearchValue(nextSearch);
      setSearch(nextSearch);
    },
    [setSearch]
  );

  return (
    <Box onKeyDown={onKeyDown}>
      {!hideSearch && (
        <Box
          p="0.1rem"
          sx={{
            borderBottom: `1px solid ${borderColor}`,
          }}
        >
          <Input
            ref={inputRef}
            leftSection={<SearchIcon color={colors.neutral[5]} size={19} />}
            placeholder={searchPlaceholder}
            value={searchValue}
            setValue={handleSearchChange}
            styles={{
              input: {
                border: 'none',
                borderBottomRightRadius: 0,
                borderBottomLeftRadius: 0,
                '&:focus': {
                  outline: 'none',
                  boxShadow: 'none!important',
                },
              },
            }}
          />
        </Box>
      )}
      {customActionTop && (
        <Box sx={{ borderBottom: `1px solid ${borderColor}` }}>
          <SelectButton
            onClick={() => customActionTop.onSubmit(searchValue)}
            isFocused={highlightedIndex === 0}
            isActive={false}
            value={{ ...customActionTop, value: 'customAction' }}
            disabled={undefined}
          />
        </Box>
      )}
      <Box>
        <ScrollArea
          h={parentHeight}
          type="auto"
          scrollbars="y"
          offsetScrollbars="present"
          viewportRef={viewportRef}
        >
          {showSkeletons ? (
            <Stack gap="xs" p={5}>
              <Skeleton height={15} w="100%" />
              <Skeleton height={15} w="100%" />
              <Skeleton height={15} w="100%" />
            </Stack>
          ) : error ? (
            <Group
              sx={{ height: '100%' }}
              p={5}
              pt="sm"
              wrap="nowrap"
              justify="center"
            >
              <Center>
                <Icon
                  icon="AlertTriangleIcon"
                  color={(theme) => theme.colors.red[6]}
                  size={18}
                />
              </Center>
              <Text component="span" size="sm" c="red">
                {error.message}
              </Text>
            </Group>
          ) : rows.length === 0 ? (
            <Center sx={{ height: '100%' }} p={5} pt="sm">
              <Text component="span" size="sm" c="gray">
                {noOptionsText}
              </Text>
            </Center>
          ) : (
            <Box
              style={{
                height: listHeight,
                position: 'relative',
                width: '100%',
              }}
            >
              {virtualItems.map((virtualRow) => {
                const index = virtualRow.index;
                const row = renderRows[index];
                const isLoaderRow = index > renderRows.length - 1;
                const isStickyGroupRow =
                  !isLoaderRow && groupRowIndexSet.has(index);
                const isActiveStickyGroupRow =
                  isStickyGroupRow &&
                  activeStickyGroupRowIndexRef.current === index;

                if (!isLoaderRow && !row) return null;

                return (
                  <div
                    key={virtualRow.key}
                    data-index={index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: isActiveStickyGroupRow ? 'sticky' : 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: isActiveStickyGroupRow
                        ? undefined
                        : `translateY(${virtualRow.start}px)`,
                      zIndex: isStickyGroupRow ? 1 : undefined,
                      backgroundColor: isStickyGroupRow
                        ? colorScheme === 'dark'
                          ? colors.dark[7]
                          : theme.white
                        : undefined,
                    }}
                  >
                    {isLoaderRow ? (
                      hasNextPage ? (
                        <Box px="xs" pb={5}>
                          <Skeleton height={15} w="100%" />
                        </Box>
                      ) : null
                    ) : row?.type === 'group' ? (
                      <GroupHeader
                        label={row.label}
                        borderColor={borderColor}
                        dataTestId={
                          isActiveStickyGroupRow
                            ? SELECT_DROPDOWN_STICKY_GROUP_TEST_ID
                            : undefined
                        }
                      />
                    ) : (
                      <>
                        <ListItem
                          item={row.item}
                          activeValue={
                            value as SelectValueProps<string>['value']
                          }
                          isFocused={
                            highlightedIndex === row.optionIndex + listOffset
                          }
                          setValue={
                            setValue as SelectValueProps<string>['setValue']
                          }
                          ref={(ref) => {
                            if (ref) {
                              buttonRefs.current[row.item.value] = ref;
                            }
                          }}
                        />
                        {pinnedValues.has(row.item.value) &&
                          !!rows[row.optionIndex + 1] &&
                          !pinnedValues.has(
                            rows[row.optionIndex + 1].value
                          ) && <Divider />}
                      </>
                    )}
                  </div>
                );
              })}
            </Box>
          )}
        </ScrollArea>
      </Box>

      {!!customBottomActions?.length && (
        <Box sx={{ borderTop: `1px solid ${borderColor}` }}>
          {customBottomActions.map(({ id, ...action }, index) => (
            <SelectButton
              onClick={() => action.onSubmit(searchValue)}
              isFocused={highlightedIndex === -(index + 1)}
              isActive={false}
              value={{ ...action, value: 'customAction' }}
              disabled={undefined}
              key={id}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const GroupHeader = ({
  label,
  borderColor,
  dataTestId,
}: {
  label: string | null;
  borderColor: string;
  dataTestId?: string;
}) => {
  if (!label) return null;

  return (
    <Text
      data-testid={dataTestId}
      component="span"
      fw={500}
      px="xs"
      pt={10}
      ml={-3}
      pb={5}
      size="sm"
      display="block"
      sx={{ borderBottom: `1px solid ${borderColor}` }}
    >
      {label}
    </Text>
  );
};

type ListItemProps = {
  setValue: SelectValueProps<string>['setValue'];
  isFocused: boolean;
  item: SelectItem;
  activeValue: SelectValueProps<string>['value'];
};

const ListItem = forwardRef<HTMLButtonElement, ListItemProps>(
  ({ activeValue, setValue, isFocused, item }, ref) => {
    const isActive = useMemo<boolean>(() => {
      const currentValue = activeValue;
      let values: SelectItem[] = [];

      if (Array.isArray(currentValue)) values = currentValue;
      else values = currentValue ? [currentValue] : [];

      return values.some((value) => value.value === item?.value);
    }, [activeValue, item]);

    const handleSetValue = useCallback(() => {
      if (Array.isArray(activeValue)) {
        const included = activeValue.some(
          (value) => value.value === item.value
        );

        if (included)
          setValue(
            activeValue.filter((value) => value.value !== item.value) as any,
            {
              type: 'removed',
              value: item,
            }
          );
        else
          setValue(activeValue.concat(item) as any, {
            type: 'added',
            value: item,
          });
      } else {
        if (activeValue?.value === item.value)
          setValue(null as any, { type: 'removed', value: item });
        else setValue(item as any, { type: 'added', value: item });
      }
    }, [item, activeValue, setValue]);

    if (!item) return null;

    if (typeof item.disabled === 'string') {
      return (
        <Tooltip
          label={item.disabled}
          withinPortal
          position="right"
          withArrow
          maw={400}
          multiline
        >
          <SelectButton
            ref={ref}
            isActive={isActive}
            isFocused={isFocused}
            value={item}
            onClick={handleSetValue}
            disabled={item.disabled}
          />
        </Tooltip>
      );
    }

    return (
      <SelectButton
        ref={ref}
        isActive={isActive}
        isFocused={isFocused}
        value={item}
        onClick={handleSetValue}
        disabled={item.disabled}
      />
    );
  }
);

const SelectButton = forwardRef<
  HTMLButtonElement,
  {
    value: SelectItem;
    onClick: () => void;
    isFocused: boolean;
    isActive: boolean;
    disabled: boolean | string | undefined;
  }
>(({ onClick, value, isActive, isFocused, disabled }, ref) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  if (!value?.value) return null;

  const { label, description, icon } = value;

  return (
    <Box
      component="button"
      disabled={!!disabled}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        onClick();
      }}
      ref={ref}
      type="button"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingInline: theme.spacing.xs,
        paddingBlock: 5,
        border: 'none',
        width: '100%',
        textAlign: 'left',
        cursor: disabled ? undefined : 'pointer',
        backgroundColor:
          isFocused && !disabled ? theme.colors.neutral[1] : 'transparent',
        ':hover': {
          backgroundColor:
            colorScheme === 'dark'
              ? theme.colors.neutral[7]
              : theme.colors.neutral[1],
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: rem(8),
          flexWrap: 'nowrap',
          width: '100%',
        }}
      >
        {icon}
        <Text
          component="span"
          size="sm"
          sx={{
            color: value.color,
            wordBreak: 'break-word',
            flex: 1,
            lineHeight: description ? 1.2 : undefined,
          }}
        >
          {label}
          {description && (
            <>
              <br />
              <Text
                size="xs"
                c={
                  disabled
                    ? undefined
                    : colorScheme === 'dark'
                      ? theme.colors.neutral[4]
                      : 'gray'
                }
                component="span"
              >
                {description}
              </Text>
            </>
          )}
        </Text>
      </Box>
      {isActive && (
        <CheckIcon
          size={18}
          color={theme.colors.neutral[5]}
          sx={{ flexShrink: 0 }}
        />
      )}
    </Box>
  );
});

function useKeyboardNavigation<TSelectValue extends string>({
  items,
  optionRenderIndexes,
  virtualizer,
  customTopActionSubmit,
  customBottomActions,
  search,
  listOffset,
}: {
  items: SelectItem<TSelectValue>[];
  optionRenderIndexes: number[];
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  customTopActionSubmit:
    | NonNullable<CustomAction['customActionTop']>['onSubmit']
    | undefined;
  customBottomActions:
    | NonNullable<CustomAction['customBottomActions']>
    | undefined;
  search: string;
  listOffset: number;
}) {
  const [highlightedIndex, setHighlightedIndex] = useState<number | undefined>(
    undefined
  );
  const buttonRefs = useRef<Record<string, HTMLButtonElement>>({});

  const bottomActionCount = customBottomActions?.length ?? 0;

  const setHighlight = useCallback(
    (nextIndex: number | undefined) => {
      setHighlightedIndex(nextIndex);

      if (nextIndex === undefined || nextIndex < 0) return;

      const listIndex = nextIndex - listOffset;
      if (listIndex < 0 || listIndex >= items.length) return;

      const optionRenderIndex = optionRenderIndexes[listIndex];
      if (optionRenderIndex === undefined) return;

      virtualizer.scrollToIndex(optionRenderIndex);
    },
    [items.length, listOffset, optionRenderIndexes, virtualizer]
  );

  const handleEnter = useCallback(() => {
    if (highlightedIndex === undefined) return;

    if (highlightedIndex < 0) {
      customBottomActions?.[Math.abs(highlightedIndex) - 1]?.onSubmit(search);
      return;
    }

    if (customTopActionSubmit && highlightedIndex === 0) {
      customTopActionSubmit(search);
      return;
    }

    const listIndex = highlightedIndex - listOffset;
    const item = items[listIndex];

    if (!item) return;

    buttonRefs.current[item.value]?.click();
  }, [
    highlightedIndex,
    customBottomActions,
    customTopActionSubmit,
    search,
    items,
    listOffset,
  ]);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      switch (event.code) {
        case 'Enter':
          handleEnter();
          break;
        case 'ArrowUp': {
          if (highlightedIndex === undefined || highlightedIndex === 0) {
            if (bottomActionCount > 0) {
              setHighlight(-bottomActionCount);
            } else {
              setHighlight(undefined);
            }
            break;
          }

          if (highlightedIndex < 0) {
            if (highlightedIndex === -1) {
              if (items.length > 0 || customTopActionSubmit) {
                setHighlight(0);
              } else {
                setHighlight(undefined);
              }
            } else {
              setHighlight(highlightedIndex + 1);
            }
            break;
          }

          setHighlight(highlightedIndex - 1);
          break;
        }
        case 'ArrowDown': {
          if (highlightedIndex === undefined) {
            if (items.length > 0 || customTopActionSubmit) {
              setHighlight(0);
            } else if (bottomActionCount > 0) {
              setHighlight(-1);
            }
            break;
          }

          if (highlightedIndex < 0) {
            if (
              bottomActionCount === 0 ||
              highlightedIndex === -bottomActionCount
            ) {
              setHighlight(0);
            } else {
              setHighlight(highlightedIndex - 1);
            }
            break;
          }

          const lastListIndex = listOffset + items.length - 1;
          if (highlightedIndex >= lastListIndex) {
            if (bottomActionCount > 0) setHighlight(-1);
            break;
          }

          setHighlight(highlightedIndex + 1);
          break;
        }
      }
    },
    [
      highlightedIndex,
      bottomActionCount,
      items.length,
      customTopActionSubmit,
      setHighlight,
      handleEnter,
      listOffset,
    ]
  );

  useEffect(() => {
    setHighlightedIndex(undefined);
  }, [search]);

  return {
    buttonRefs,
    highlightedIndex,
    onKeyDown,
  };
}
